import type {
  CreditNoteStatus,
  ExtractionStatus,
  InboundStatus,
  InboundTrackingSide,
  MissingItemsResolution,
  PoStatus,
  ReceiptOutcome,
} from "./enums";
import type { InboundMessage } from "./inbound";

// Rules of the procurement domain the apps apply too (pattern.md §4): the browser offers only what the domain would
// accept, with the same code. The domain still enforces them (workers/procurement, purchase-order.rules.ts).

const noMove = {
  generated: [],
  approved: [],
  published: [],
  in_validation: [],
  confirmed: [],
  closed: [],
  cancelled: [],
} as const satisfies Record<PoStatus, readonly PoStatus[]>;

/**
 * Which status each action on a PO moves from and to (spec v0.2 §5.3, FR-M5-02…07). One table per action, so an
 * action cannot borrow another's move.
 */
export const purchaseOrderTransitions = {
  approve: { ...noMove, generated: ["approved"] },
  publish: { ...noMove, approved: ["published"] },
  /** Also from `approved`: a mistake found before the publication does not need the supplier to see it first. */
  rework: { ...noMove, approved: ["generated"], published: ["generated"], in_validation: ["generated"] },
  cancel: {
    ...noMove,
    generated: ["cancelled"],
    approved: ["cancelled"],
    published: ["cancelled"],
    in_validation: ["cancelled"],
  },
  /** FR-M5-05: the first proposal of the supplier opens the validation, the next ones find it open. */
  propose: { ...noMove, published: ["in_validation"], in_validation: ["in_validation"] },
  /** FR-M5-07: as published, or once every proposal has its answer. */
  confirm: { ...noMove, published: ["confirmed"], in_validation: ["confirmed"] },
  /** FR-M5-06: proposals are withdrawn and decided while the PO is in validation, which it does not leave by that. */
  answer: { ...noMove, in_validation: ["in_validation"] },
  close: { ...noMove, confirmed: ["closed"] },
  /** Lines are closed one by one only once the PO is confirmed; it closes with its last open one. */
  closeLines: { ...noMove, confirmed: ["confirmed", "closed"] },
} as const satisfies Record<string, Record<PoStatus, readonly PoStatus[]>>;

export type PurchaseOrderAction = keyof typeof purchaseOrderTransitions;

/** Whether `action` can start from `status`. */
export function canMovePurchaseOrder(action: PurchaseOrderAction, status: PoStatus): boolean {
  return purchaseOrderTransitions[action][status].length > 0;
}

/**
 * The statuses of a PO that is over: every other one is open. A supplier without its number on Business Central and
 * with an open PO waits for someone to register it (ADR-178 §3.4, ADR-189): party applies the same list in SQL.
 */
export const endedPoStatuses = ["closed", "cancelled"] as const satisfies readonly PoStatus[];

/** Header and lines change only while the PO is `generated`. */
export function canEditPurchaseOrder(po: { status: PoStatus }): boolean {
  return po.status === "generated";
}

/** The cost of a line no longer changes once something of it was received or invoiced (`cost_locked`). */
export function costLocked(line: { qtyReceived: number; qtyBilled: number }): boolean {
  return line.qtyReceived > 0 || line.qtyBilled > 0;
}

/** A line with receipts is not taken out of the order (`already_received`): it is closed, once the PO is confirmed. */
export function hasReceipts(line: { qtyReceived: number }): boolean {
  return line.qtyReceived > 0;
}

const noInboundMove = {
  draft: [],
  documents_uploaded: [],
  pending_authorization: [],
  authorized: [],
  lines_captured: [],
  in_exception: [],
  three_way_passed: [],
  ddt_generated: [],
  in_transit: [],
  partially_received: [],
  received: [],
  closed: [],
  cancelled: [],
} as const satisfies Record<InboundStatus, readonly InboundStatus[]>;

/**
 * Which status each action on an inbound moves from and to (FR-M11-01, B-04, ADR-128). The supplier sends a draft:
 * to Logistics, or first to the brand when a line is out of tolerance; the brand authorizes it or sends it back.
 */
export const inboundTransitions = {
  submit: { ...noInboundMove, draft: ["documents_uploaded", "pending_authorization"] },
  authorize: { ...noInboundMove, pending_authorization: ["authorized"] },
  refuse: { ...noInboundMove, pending_authorization: ["draft"] },
  /** Before Logistics takes the lines: after that, what arrives is no longer only the supplier's to take back. */
  cancel: {
    ...noInboundMove,
    draft: ["cancelled"],
    documents_uploaded: ["cancelled"],
    pending_authorization: ["cancelled"],
    authorized: ["cancelled"],
  },
  /**
   * FR-M11-02, ADR-131: Logistics types the packing list once the inbound waits for it, and again after a failed
   * check; a new packing list asks for a new check.
   */
  capture: {
    ...noInboundMove,
    documents_uploaded: ["lines_captured"],
    authorized: ["lines_captured"],
    lines_captured: ["lines_captured"],
    in_exception: ["lines_captured"],
  },
  /** FR-M11-05…07: the check of the packing list Logistics typed. */
  check: { ...noInboundMove, lines_captured: ["three_way_passed", "in_exception"] },
  /** Operations forces a failed check, with its reason (gate `procurement.threeWayCheck`). */
  override: { ...noInboundMove, in_exception: ["three_way_passed"] },
  /**
   * ADR-137: Logistics sends the inbound to the warehouse (I-08, the inbound DDT) and to Business Central (I-23, I-24)
   * once its check let it through. An inbound of extra units goes to Business Central only, and closes (ADR-139).
   */
  dispatch: { ...noInboundMove, three_way_passed: ["ddt_generated", "closed"] },
  /** The warehouse accepted the ASN (I-08 §7): only the outcome of TIL moves it. */
  transit: { ...noInboundMove, ddt_generated: ["in_transit"] },
  /**
   * I-11 §7, ADR-138: a receipt of the warehouse, whose message may arrive before the outcome of the ASN; `received`
   * once the warehouse expects nothing more on the ASN. Only the receipts of the warehouse move it.
   */
  receive: {
    ...noInboundMove,
    ddt_generated: ["partially_received", "received"],
    in_transit: ["partially_received", "received"],
    partially_received: ["partially_received", "received"],
  },
  /** FR-M11-11, ADR-139: the warehouse received what was declared, each line within its tolerance (outcome A). */
  close: { ...noInboundMove, received: ["closed"] },
  /**
   * ADR-139: Logistics confirms an outcome with a difference (gate `procurement.inboundException`); the inbound closes
   * unless units are missing, which wait for the supplier's choice (ADR-140).
   */
  resolve: { ...noInboundMove, received: ["received", "closed"] },
  /**
   * ADR-140: the supplier's side chooses what happens to the missing units, and the inbound closes; a credit note lives
   * on, with its own status, on Finance's list.
   */
  settle: { ...noInboundMove, received: ["closed"] },
  /**
   * ADR-182, ADR-243: Logistics releases the units on hold of an inbound of extra units the supplier does not complete,
   * with its reason, until it is sent to Business Central: its units are free again and I-23 leaves, while the inbound
   * stays where it is, open for the supplier's invoice (I-24).
   */
  release: {
    ...noInboundMove,
    draft: ["draft"],
    documents_uploaded: ["documents_uploaded"],
    lines_captured: ["lines_captured"],
    in_exception: ["in_exception"],
    three_way_passed: ["three_way_passed"],
  },
  /** Logistics sends the inbound back to the supplier, with its reason, until the check passes (ADR-131). */
  return: {
    ...noInboundMove,
    documents_uploaded: ["draft"],
    authorized: ["draft"],
    lines_captured: ["draft"],
    in_exception: ["draft"],
  },
} as const satisfies Record<string, Record<InboundStatus, readonly InboundStatus[]>>;

export type InboundAction = keyof typeof inboundTransitions;

/** Whether `action` can start from `status`. */
export function canMoveInbound(action: InboundAction, status: InboundStatus): boolean {
  return inboundTransitions[action][status].length > 0;
}

/**
 * The inbounds that wait for Logistics to type their packing list for the first time: sent within the tolerance, or
 * authorized by the brand (FR-M11-02). The `toCapture` filter of the list, and a queue of the home.
 */
export const inboundsToCapture = ["documents_uploaded", "authorized"] as const satisfies readonly InboundStatus[];

/**
 * The readings of the extraction tool that keep an inbound out of the queue of the packing lists to type (ADR-233):
 * one asked for and not back yet, and one whose lines wait for the review, a queue of its own.
 */
export const extractionsHoldingCapture = ["requested", "to_review"] as const satisfies readonly ExtractionStatus[];

/**
 * Whether the packing list of an inbound may be sent to the extraction tool (B-50, ADR-233): before Logistics first
 * typed it (`inboundsToCapture`), and not while a reading is out or waits for its review. A reading that failed, or one
 * already confirmed, may be asked again: its result replaces the lines.
 */
export function canRequestExtraction(inbound: {
  status: InboundStatus;
  extractionStatus: ExtractionStatus | null;
}): boolean {
  return (
    (inboundsToCapture as readonly InboundStatus[]).includes(inbound.status) &&
    (inbound.extractionStatus === null ||
      !(extractionsHoldingCapture as readonly ExtractionStatus[]).includes(inbound.extractionStatus))
  );
}

/** Whether Logistics may confirm the lines the tool read as they are: only while they wait for its review. */
export function canConfirmExtraction(inbound: {
  status: InboundStatus;
  extractionStatus: ExtractionStatus | null;
}): boolean {
  return (
    (inboundsToCapture as readonly InboundStatus[]).includes(inbound.status) && inbound.extractionStatus === "to_review"
  );
}

/**
 * Which line of the inbound a line of the packing list is on (ADR-131): the PO line it names, or else the one line
 * whose SKU has its EAN. An EAN on two lines of the inbound (the same SKU on two PO lines) matches neither, and
 * Logistics names the PO line. The domain writes the match; the apps show it as they type.
 */
export function packingListMatcher<L extends { poLineId: string; ean: string | null }>(inboundLines: readonly L[]) {
  const byPoLine = new Map(inboundLines.map((line) => [line.poLineId, line]));
  const byEan = new Map<string, L | null>();
  for (const line of inboundLines) {
    if (line.ean !== null) byEan.set(line.ean, byEan.has(line.ean) ? null : line);
  }
  return (line: { poLineId?: string | undefined; ean?: string | undefined }): L | undefined => {
    if (line.poLineId !== undefined) return byPoLine.get(line.poLineId);
    return line.ean === undefined ? undefined : (byEan.get(line.ean) ?? undefined);
  };
}

/** An inbound announces a shipment of a PO the supplier confirmed (FR-M11-01): before, there is nothing to ship. */
export function canAnnounceInbound(po: { status: PoStatus }): boolean {
  return po.status === "confirmed";
}

/** Header, lines and documents of an inbound change only while it is a `draft`. */
export function canEditInbound(inbound: { status: InboundStatus }): boolean {
  return inbound.status === "draft";
}

/**
 * The incoterms under which the seller arranges the main carriage, the C and D terms of Incoterms 2020: with them the
 * supplier books the carrier and writes the tracking of its inbound (B-51, ADR-212). Under the others (E and F, EXW and
 * FCA in Nexxt today) Tomorrow's forwarder carries the goods.
 */
export const supplierCarriageIncoterms = ["CPT", "CIP", "CFR", "CIF", "DAP", "DPU", "DDP"] as const;

/**
 * Who writes the tracking of an inbound, by the incoterm of its PO (B-51, ADR-212): the supplier with a C or D term,
 * Tomorrow with any other, and without one. Tomorrow may write it for the supplier too; the reminder goes to this side.
 */
export const inboundTrackingSide = (incoterm: string | null): InboundTrackingSide =>
  incoterm !== null && (supplierCarriageIncoterms as readonly string[]).includes(incoterm) ? "supplier" : "tomorrow";

/**
 * The statuses an inbound is on its way to the warehouse in (ADR-212): from the submission until the warehouse receives
 * something. Its tracking is written in them, and missing in them it is reminded.
 */
export const trackableInboundStatuses = [
  "documents_uploaded",
  "pending_authorization",
  "authorized",
  "lines_captured",
  "in_exception",
  "three_way_passed",
  "ddt_generated",
  "in_transit",
] as const satisfies readonly InboundStatus[];

/**
 * Whether an inbound is on its way and not the inbound of extra units already in: then its tracking is written
 * (ADR-212), and its ready date and pickup contact move (B-89, ADR-244).
 */
export function canTrackInbound(inbound: { status: InboundStatus; isExtraUnits: boolean }): boolean {
  return !inbound.isExtraUnits && (trackableInboundStatuses as readonly InboundStatus[]).includes(inbound.status);
}

/**
 * An inbound of extra units (ADR-139) carries units the warehouse already holds, on hold until the supplier invoices
 * them: its lines are what was received, and it is never cancelled.
 */
export function canChangeInboundLines(inbound: { status: InboundStatus; isExtraUnits: boolean }): boolean {
  return canEditInbound(inbound) && !inbound.isExtraUnits;
}

export function canCancelInbound(inbound: { status: InboundStatus; isExtraUnits: boolean }): boolean {
  return !inbound.isExtraUnits && canMoveInbound("cancel", inbound.status);
}

/** An inbound as its release reads it (ADR-243): `unitsReleasedAt` set once Logistics released its units. */
interface ReleasableInbound {
  status: InboundStatus;
  isExtraUnits: boolean;
  unitsReleasedAt: string | Date | null;
}

/**
 * Whether Logistics can release the units on hold of an inbound (ADR-182, ADR-243): only of an inbound of extra units,
 * once, until it is sent to Business Central. A released one holds nothing, and waits for the supplier's invoice.
 */
export function canReleaseOnHoldUnits(inbound: ReleasableInbound): boolean {
  return inbound.isExtraUnits && inbound.unitsReleasedAt === null && canMoveInbound("release", inbound.status);
}

/**
 * The messages an inbound sends (ADR-137): I-23 and I-24 to Business Central, I-08 to the warehouse; not the last for
 * an inbound of extra units, whose units the warehouse already holds (ADR-139).
 */
export const inboundMessagesOf = (inbound: { isExtraUnits: boolean }): readonly InboundMessage[] =>
  inbound.isExtraUnits ? ["I-23", "I-24"] : ["I-23", "I-24", "I-08"];

/**
 * The messages its dispatch sends (ADR-137, ADR-243): all of them, but I-23 of an inbound whose units Logistics
 * released, which left with the release; the dispatch then sends the invoice alone.
 */
export const dispatchMessagesOf = (inbound: Omit<ReleasableInbound, "status">): readonly InboundMessage[] =>
  inboundMessagesOf(inbound).filter((message) => message !== "I-23" || inbound.unitsReleasedAt === null);

/**
 * The received inbounds whose outcome has a difference Logistics has not confirmed yet (ADR-139): the `toResolve`
 * filter of the list, and a queue of the home.
 */
export const outcomesToResolve = [
  "excess",
  "shortage",
  "excess_and_shortage",
] as const satisfies readonly ReceiptOutcome[];

/** Whether an inbound has an outcome for Logistics to confirm (ADR-139): received, with a difference, not confirmed. */
export function hasOutcomeToConfirm(inbound: {
  status: InboundStatus;
  receiptOutcome: ReceiptOutcome | null;
  outcomeConfirmedAt: string | Date | null;
}): boolean {
  const { receiptOutcome } = inbound;
  return (
    inbound.status === "received" &&
    inbound.outcomeConfirmedAt === null &&
    receiptOutcome !== null &&
    (outcomesToResolve as readonly ReceiptOutcome[]).includes(receiptOutcome)
  );
}

/** The outcomes with units missing beyond the tolerance (ADR-139). */
export const shortageOutcomes = ["shortage", "excess_and_shortage"] as const satisfies readonly ReceiptOutcome[];

export const hasShortage = (outcome: ReceiptOutcome | null): boolean =>
  outcome !== null && (shortageOutcomes as readonly ReceiptOutcome[]).includes(outcome);

/**
 * Whether the supplier's side has the missing units of an inbound to settle (ADR-140): received, with units missing
 * that Logistics confirmed. The choice closes it, so a closed inbound has none.
 */
export function canSettleShortage(inbound: {
  status: InboundStatus;
  receiptOutcome: ReceiptOutcome | null;
  outcomeConfirmedAt: string | Date | null;
}): boolean {
  return inbound.status === "received" && inbound.outcomeConfirmedAt !== null && hasShortage(inbound.receiptOutcome);
}

/**
 * What the supplier of a brand may choose for missing units (ADR-140): a distribution brand takes a credit note only,
 * as in today's portal; the others also a later shipment, or both.
 */
export function missingItemsChoices(brand: { distribution: boolean }): readonly MissingItemsResolution[] {
  return brand.distribution ? ["credit_note"] : ["credit_note", "ship_later", "credit_note_and_ship_later"];
}

/** The choices that ask the supplier for a credit note (ADR-140). */
export const creditNoteResolutions = [
  "credit_note",
  "credit_note_and_ship_later",
] as const satisfies readonly MissingItemsResolution[];

export const includesCreditNote = (resolution: MissingItemsResolution | null): boolean =>
  resolution !== null && (creditNoteResolutions as readonly MissingItemsResolution[]).includes(resolution);

/**
 * The choices of a later shipment: the supplier announces the missing units again in a new inbound, so they no longer
 * count among those the received one announced (ADR-140).
 */
export const shipLaterResolutions = [
  "ship_later",
  "credit_note_and_ship_later",
] as const satisfies readonly MissingItemsResolution[];

/**
 * The supplier's side sends the file of a credit note Finance has not received (ADR-140): the first, one that
 * replaces it while Finance has not decided, or a new one after a rejection.
 */
export const canSendCreditNote = (note: { status: CreditNoteStatus }): boolean => note.status !== "received";

/**
 * A credit note waits for the file of the supplier's side (ADR-140): never sent, or rejected by Finance. The `toSend`
 * filter of the list, and a queue of the home.
 */
export const waitsForCreditNoteFile = (note: { status: CreditNoteStatus; hasFile: boolean }): boolean =>
  note.status === "rejected" || (note.status === "requested" && !note.hasFile);

/** Finance decides a credit note once its file is there, until it does (ADR-140). */
export const canDecideCreditNote = (note: { status: CreditNoteStatus; hasFile: boolean }): boolean =>
  note.status === "requested" && note.hasFile;

/** A delivery tolerance band (`procurement.tolerance_band`): on the quantity of a PO line, `tolerancePct` percent. */
export interface ToleranceBand {
  minQty: number;
  /** Null: no upper bound. */
  maxQty: number | null;
  /** As the DB returns a `numeric(5,2)`, e.g. `5.00`. */
  tolerancePct: string;
}

const inBand = (qty: number) => (band: ToleranceBand) =>
  qty >= band.minQty && (band.maxQty === null || qty <= band.maxQty);

/**
 * The units a PO line may differ by without the brand's authorization (V-02, ADR-128): the brand's band for its
 * quantity, otherwise the global one, otherwise none. `ceil(qty × pct / 100)`, in hundredths of a percent so that no
 * floating point rounding adds a unit.
 */
export function acceptedUnits(qtyPo: number, bands: { brand: ToleranceBand[]; global: ToleranceBand[] }): number {
  const band = bands.brand.find(inBand(qtyPo)) ?? bands.global.find(inBand(qtyPo));
  if (!band) return 0;
  const hundredths = Math.round(Number(band.tolerancePct) * 100);
  return Math.ceil((qtyPo * hundredths) / 10_000);
}

/**
 * Whether a submission waits for the brand (B-04): a line out of its tolerance, unless the brand is the supplier itself
 * (B-30), which declared the difference; Logistics then checks it at the receipt.
 */
export function waitsForBrand(p: { brandIsSupplier: boolean; outOfTolerance: number }): boolean {
  return !p.brandIsSupplier && p.outOfTolerance > 0;
}

/**
 * Whether what the supplier declares for a PO line, over all its inbounds sent so far and this one, stays within the
 * tolerance: never more units than ordered plus the accepted ones, and never fewer than ordered minus the accepted
 * ones when the supplier says nothing more will come (`shortShipmentAccepted`). A shipment in parts is not a shortage.
 */
export function withinTolerance(line: {
  qtyPo: number;
  declaredTotal: number;
  acceptedUnits: number;
  shortShipmentAccepted: boolean;
}): boolean {
  const difference = line.declaredTotal - line.qtyPo;
  if (difference > line.acceptedUnits) return false;
  return !line.shortShipmentAccepted || difference >= -line.acceptedUnits;
}

/**
 * FR-M5-02: an external Actor sees a PO only from its publication on. One taken back to `generated` leaves its lists
 * until it is published again; a cancelled one stays only if it had been published.
 */
export const PUBLISHED_STATUSES = ["published", "in_validation", "confirmed", "closed"] as const satisfies PoStatus[];

/**
 * Whether the brand and the supplier of a PO see it (FR-M5-02): the same rule `visibleTo` of procurement writes in SQL,
 * for who reads a PO from another domain (the followers of notifications, ADR-281).
 */
export function partnersSeePurchaseOrder(po: { status: PoStatus; publishedAt: Date | null }): boolean {
  const published: readonly PoStatus[] = PUBLISHED_STATUSES;
  return published.includes(po.status) || (po.status === "cancelled" && po.publishedAt !== null);
}

/**
 * Whether the brand of an inbound sees it: a draft is the supplier's own until it is sent once, and one cancelled as a
 * draft stays its own. The same rule `seenBy` of procurement writes in SQL; the supplier always sees it.
 */
export const brandSeesInbound = (inbound: { submittedAt: Date | null }): boolean => inbound.submittedAt !== null;
